/* ============================================================
   Quick capture — one line, Enter to save, and the same fast-entry grammar as
   the mobile sheet (shared/src/capture): `#project` files it, `@tag` tags it
   (creating the tag on save), a date phrase ("tomorrow", "friday 5pm", "every
   monday") schedules it. What the mobile sheet does with chips, this does in
   place: the tokens light up as they resolve (a mirror layer paints the text
   under a transparent input), a line below says what you'll get, and `#`/`@`
   open suggestions.
   ============================================================ */
import React, { useMemo, useRef, useState } from 'react';
import {
  activeToken, type CaptureToken, DAILY_SCHEDULE, maskSpans, parseCapture, parseCaptureDate, type RecurrencePreset, stripDateSpans,
} from '@habishine/shared';
import { type CaptureState, dayLabel, formatTime, todayStr, useStore, useWeekStart } from '@habishine/client-core';

import { track } from '../analytics';
import { hueVar } from '../theme/hues';
import { Icon } from './Icon';

export const CAPTURE_INPUT_ID = 'habishine-capture';

const WEEKDAY: Record<string, string> = { MO: 'Mon', TU: 'Tue', WE: 'Wed', TH: 'Thu', FR: 'Fri', SA: 'Sat', SU: 'Sun' };
function repeatLabel(p: RecurrencePreset): string {
  const every = p.interval > 1 ? `every ${p.interval} ` : 'every ';
  if (p.freq === 'daily') return p.interval > 1 ? `${every}days` : 'every day';
  if (p.freq === 'monthly') return p.interval > 1 ? `${every}months` : 'every month';
  const days = p.byday.map((d) => WEEKDAY[d] ?? d).join(', ');
  return p.byday.length >= 5 && !p.byday.includes('SA') && !p.byday.includes('SU') ? 'weekdays' : `${every}${p.interval > 1 ? 'weeks on ' : ''}${days}`;
}

type Seg = { text: string; kind?: 'project' | 'tag' | 'newtag' | 'date'; hue?: string };

// the input and its mirror must share every metric that moves a glyph
const TEXT_CLASS = 'py-1.5 text-[15.5px] leading-6 tracking-normal';

export function Capture({
  defaultToday,
  projectId = null,
  day: dayOnScreen,
  placeholder,
  className = '',
}: {
  /** where an undated capture lands: the day on screen, or the backlog */
  defaultToday: boolean;
  /** file every capture under this project unless `#other` says otherwise */
  projectId?: string | null;
  day?: string;
  placeholder?: string;
  className?: string;
}) {
  const { state, activeProjects, activeTags, createTag, saveCapture } = useStore();
  const { weekStartsOn } = useWeekStart();
  const [text, setText] = useState('');
  const [caret, setCaret] = useState(0);
  const [cursor, setCursor] = useState(0);
  const [focused, setFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const ctx = useMemo(() => ({ projects: activeProjects(), tags: activeTags() }), [state]);

  /* ---- what the text means right now ---- */
  const parsed = useMemo(() => parseCapture(text, ctx), [text, ctx]);
  // dates are parsed over the text with the tokens blanked, so their spans
  // index the ORIGINAL string and can be painted in place
  const masked = useMemo(() => maskSpans(text, parsed.cuts), [text, parsed]);
  const date = useMemo(() => (text.trim() ? parseCaptureDate(masked, todayStr(), weekStartsOn) : null), [masked, text, weekStartsOn]);
  const name = useMemo(() => (date ? stripDateSpans(masked, date.spans) : masked).replace(/\s+/g, ' ').trim(), [masked, date]);
  const project = parsed.projectId ? ctx.projects.find((p) => p.id === parsed.projectId) ?? null : null;
  const scopeProject = !project && projectId ? ctx.projects.find((p) => p.id === projectId) ?? null : null;
  const tags = parsed.tagIds.map((id) => ctx.tags.find((t) => t.id === id)).filter((t): t is NonNullable<typeof t> => !!t);
  const landsOn = date?.day ?? (defaultToday ? dayOnScreen ?? todayStr() : null);

  /* ---- the painted text ---- */
  const segments = useMemo<Seg[]>(() => {
    const marks: { start: number; end: number; kind: Seg['kind']; hue?: string }[] = [];
    for (const c of parsed.cuts) {
      const row = c.kind === 'project' ? ctx.projects.find((p) => p.id === c.id) : ctx.tags.find((t) => t.id === c.id);
      const hue = (row as { hue?: string } | undefined)?.hue;
      marks.push({ start: c.start, end: c.end, kind: c.kind === 'tag' && !c.id ? 'newtag' : c.kind, hue });
    }
    for (const s of date?.spans ?? []) marks.push({ start: s.start, end: s.end, kind: 'date' });
    marks.sort((a, b) => a.start - b.start);
    const out: Seg[] = [];
    let i = 0;
    for (const m of marks) {
      if (m.start < i) continue;
      if (m.start > i) out.push({ text: text.slice(i, m.start) });
      out.push({ text: text.slice(m.start, m.end), kind: m.kind, hue: m.hue });
      i = m.end;
    }
    if (i < text.length) out.push({ text: text.slice(i) });
    return out;
  }, [text, parsed, date, ctx]);

  /* ---- suggestions for the token under the caret ---- */
  const token: CaptureToken | null = focused ? activeToken(text, ctx, caret) : null;
  const suggestions = useMemo(() => {
    if (!token) return [];
    const q = token.query.toLowerCase();
    const rows = token.kind === 'project' ? ctx.projects : ctx.tags;
    const hits = rows.filter((r) => r.name.toLowerCase().includes(q)).slice(0, 6).map((r) => ({ id: r.id, name: r.name, hue: (r as { hue: string }).hue, create: false }));
    if (token.kind === 'tag' && q && !rows.some((r) => r.name.toLowerCase() === q)) hits.push({ id: 'new', name: token.query, hue: 'teal', create: true });
    return hits;
  }, [token, ctx]);

  const pick = (s: { name: string }) => {
    if (!token) return;
    const prefix = token.kind === 'project' ? '#' : '@';
    const before = text.slice(0, token.start);
    const after = text.slice(token.end).replace(/^\s/, '');
    const next = `${before}${prefix}${s.name} ${after}`;
    const at = before.length + 1 + s.name.length + 1;
    setText(next);
    setCaret(at);
    requestAnimationFrame(() => inputRef.current?.setSelectionRange(at, at));
  };

  const save = () => {
    if (!name) return;
    const tagIds = [...parsed.tagIds, ...parsed.newTagNames.map((n) => createTag(n))];
    const cap: CaptureState = {
      emoji: '', hue: 'purple', today: landsOn !== null, habit: false, target: 1, schedule: DAILY_SCHEDULE,
      recurrence: date?.recurrence ?? null, tagIds, projectId: parsed.projectId ?? projectId, remindAt: date?.remindAt ?? null,
    };
    saveCapture(name, cap, landsOn ?? undefined);
    track('task_created', { scheduled: landsOn !== null });
    setText('');
    setCaret(0);
  };

  const onKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (suggestions.length > 0) {
      if (e.key === 'ArrowDown') { e.preventDefault(); setCursor((c) => (c + 1) % suggestions.length); return; }
      if (e.key === 'ArrowUp') { e.preventDefault(); setCursor((c) => (c - 1 + suggestions.length) % suggestions.length); return; }
      if (e.key === 'Tab' || e.key === 'Enter') { e.preventDefault(); const s = suggestions[cursor] ?? suggestions[0]; if (s) pick(s); return; }
    }
    if (e.key === 'Enter') { e.preventDefault(); save(); }
    else if (e.key === 'Escape') (e.target as HTMLInputElement).blur();
  };
  const syncCaret = (el: HTMLInputElement) => setCaret(el.selectionStart ?? el.value.length);

  const segColor = (s: Seg): React.CSSProperties | undefined => {
    if (s.kind === 'project') return { color: hueVar(s.hue as never, 'k'), background: 'var(--frog-wash)', borderRadius: 6 };
    if (s.kind === 'tag') return { color: hueVar(s.hue as never, 'k'), background: 'var(--frog-wash)', borderRadius: 6 };
    if (s.kind === 'newtag') return { color: 'var(--meta)', textDecoration: 'underline dashed', textUnderlineOffset: 3 };
    if (s.kind === 'date') return { color: 'var(--frog)', fontWeight: 600 };
    return undefined;
  };

  const hasChips = !!text.trim();
  return (
    <div className={`relative ${className}`}>
      {/* the bar: a frosted card with the violet + in a lit disc; focus lifts
         it and rings it in the brand wash */}
      <div className={`surface flex items-center gap-3 rounded-row py-1.5 pl-2.5 pr-4 transition-[box-shadow,transform] duration-200 ${focused ? 'shadow-[var(--shadow-hover),0_0_0_4px_var(--frog-wash)]' : ''}`}>
        <span className={`frog-gradient flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-white transition-transform duration-200 ${focused ? 'rotate-90 scale-105' : ''}`} aria-hidden>
          <Icon name="plus" size={15} strokeWidth={2.8} />
        </span>
        <div className="relative min-w-0 flex-1">
          {/* the mirror: the same text, painted */}
          <div aria-hidden className={`pointer-events-none absolute inset-0 overflow-hidden whitespace-pre text-ink ${TEXT_CLASS}`}>
            {segments.map((s, i) => <span key={i} style={segColor(s)}>{s.text}</span>)}
          </div>
          <input
            ref={inputRef}
            id={CAPTURE_INPUT_ID}
            className={`relative w-full bg-transparent text-transparent caret-[var(--ink)] outline-none placeholder:text-placeholder ${TEXT_CLASS}`}
            placeholder={placeholder ?? 'Add a task — #project @tag, "tomorrow 5pm", "every monday"'}
            value={text}
            onChange={(e) => { setText(e.target.value); syncCaret(e.target); setCursor(0); }}
            onKeyUp={(e) => syncCaret(e.currentTarget)}
            onClick={(e) => syncCaret(e.currentTarget)}
            onFocus={() => setFocused(true)}
            onBlur={() => setTimeout(() => setFocused(false), 120)}
            onKeyDown={onKey}
            autoComplete="off"
            spellCheck={false}
          />
        </div>
        {hasChips ? (
          <button type="button" onMouseDown={(e) => { e.preventDefault(); save(); }} className="frog-gradient rounded-full px-3 py-1 text-[12px] font-bold text-white md:hidden">Add</button>
        ) : null}
        {hasChips ? <span className="hidden text-[11.5px] font-semibold text-tertiary md:inline">↵ add</span> : <kbd className="kbd hidden md:inline">N</kbd>}
      </div>

      {/* what you'll get */}
      {hasChips ? (
        <div className="fade-in mt-2 flex flex-wrap items-center gap-1.5 px-3 text-[12px] text-meta">
          <span className="font-semibold text-ink-2">{name ? `“${name}”` : 'Name?'}</span>
          <Chip icon="calendar" tone={landsOn ? 'frog' : 'meta'}>{landsOn ? dayLabel(landsOn) : 'Backlog'}</Chip>
          {date?.remindAt != null ? <Chip icon="bell" tone="frog">{formatTime(date.remindAt)}</Chip> : null}
          {date?.recurrence ? <Chip icon="clock" tone="frog">{repeatLabel(date.recurrence)}</Chip> : null}
          {project || scopeProject ? <Chip tone="meta" dot={hueVar((project ?? scopeProject)!.hue, 'c')}>{(project ?? scopeProject)!.name}</Chip> : null}
          {tags.map((t) => <Chip key={t.id} tone="meta" dot={hueVar(t.hue, 'c')}>@{t.name}</Chip>)}
          {parsed.newTagNames.map((n) => <Chip key={n} tone="meta">@{n} · new tag</Chip>)}
        </div>
      ) : null}

      {/* suggestions for the token under the caret */}
      {suggestions.length > 0 ? (
        <div className="surface-pop pop-in absolute left-12 top-full z-20 mt-1.5 w-64 rounded-[16px] p-1.5" role="listbox">
          {suggestions.map((s, i) => (
            <button
              key={s.id}
              type="button"
              role="option"
              aria-selected={i === cursor}
              onMouseDown={(e) => { e.preventDefault(); pick(s); }}
              onMouseEnter={() => setCursor(i)}
              className={`flex w-full items-center gap-2 rounded-[10px] px-2.5 py-1.5 text-left text-[13px] ${i === cursor ? 'bg-frog-soft text-ink' : 'text-ink-2'}`}
            >
              {s.create ? <Icon name="plus" size={13} strokeWidth={2.4} /> : <span className="h-2.5 w-2.5 rounded-full" style={{ background: hueVar(s.hue as never, 'c') }} />}
              <span className="truncate">{token?.kind === 'project' ? '#' : '@'}{s.name}</span>
              {s.create ? <span className="ml-auto text-[11px] text-tertiary">new tag</span> : null}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function Chip({ children, icon, tone, dot }: { children: React.ReactNode; icon?: 'calendar' | 'bell' | 'clock'; tone: 'frog' | 'meta'; dot?: string }) {
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 ${tone === 'frog' ? 'bg-frog-soft text-frog' : 'bg-card-sunk text-ink-2'}`}>
      {icon ? <Icon name={icon} size={11} strokeWidth={2.4} /> : null}
      {dot ? <span className="h-2 w-2 rounded-full" style={{ background: dot }} /> : null}
      {children}
    </span>
  );
}
